import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  legalDocumentService,
  LegalDocument,
  LegalDocumentType,
  CreateLegalDocumentData,
  UpdateLegalDocumentData,
  LegalDocumentFilters,
} from '@/services/legalDocumentService';

// ============================================
// QUERY KEYS
// ============================================

export const legalDocumentsKeys = {
  all: ['legal-documents'] as const,
  active: (type: LegalDocumentType) => ['legal-documents', 'active', type] as const,
  lists: () => ['legal-documents', 'list'] as const,
  list: (filters?: LegalDocumentFilters) => ['legal-documents', 'list', filters] as const,
  detail: (id: number) => ['legal-documents', 'detail', id] as const,
};

// ============================================
// QUERIES
// ============================================

/**
 * Get active legal document by type (public)
 * Used in public-facing Terms & Conditions and Privacy Policy pages
 */
export const useLegalDocument = (type: LegalDocumentType, enabled = true) => {
  return useQuery<LegalDocument>({
    queryKey: legalDocumentsKeys.active(type),
    queryFn: () => legalDocumentService.getActiveDocument(type),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - legal docs don't change often
    retry: 2,
  });
};

/**
 * Get all legal documents with filters (admin)
 * Used in management page to show version history
 */
export const useLegalDocuments = (filters?: LegalDocumentFilters, enabled = true) => {
  return useQuery<LegalDocument[]>({
    queryKey: legalDocumentsKeys.list(filters),
    queryFn: () => legalDocumentService.getAllDocuments(filters),
    enabled,
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: 2,
  });
};

/**
 * Get single legal document by ID (admin)
 * Used when viewing/editing specific document
 */
export const useLegalDocumentById = (id: number, enabled = true) => {
  return useQuery<LegalDocument>({
    queryKey: legalDocumentsKeys.detail(id),
    queryFn: () => legalDocumentService.getDocument(id),
    enabled,
    staleTime: 1 * 60 * 1000,
    retry: 2,
  });
};

// ============================================
// MUTATIONS
// ============================================

/**
 * Create new legal document (admin)
 */
export const useCreateLegalDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLegalDocumentData) => legalDocumentService.createDocument(data),
    onSuccess: (newDocument) => {
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: legalDocumentsKeys.lists() });

      // Optionally set the new document in cache
      queryClient.setQueryData(legalDocumentsKeys.detail(newDocument.id), newDocument);
    },
  });
};

/**
 * Update legal document (admin)
 */
export const useUpdateLegalDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateLegalDocumentData }) =>
      legalDocumentService.updateDocument(id, data),
    onSuccess: (updatedDocument) => {
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: legalDocumentsKeys.lists() });

      // Update the specific document in cache
      queryClient.setQueryData(legalDocumentsKeys.detail(updatedDocument.id), updatedDocument);
    },
  });
};

/**
 * Delete legal document (admin)
 */
export const useDeleteLegalDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => legalDocumentService.deleteDocument(id),
    onSuccess: (_, deletedId) => {
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: legalDocumentsKeys.lists() });

      // Remove from cache
      queryClient.removeQueries({ queryKey: legalDocumentsKeys.detail(deletedId) });
    },
  });
};

/**
 * Publish legal document (admin)
 * Makes document active and deactivates previous version
 */
export const usePublishLegalDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => legalDocumentService.publishDocument(id),
    onSuccess: (publishedDocument) => {
      // Invalidate all lists (active status changed)
      queryClient.invalidateQueries({ queryKey: legalDocumentsKeys.lists() });

      // Invalidate the active document query for this type
      queryClient.invalidateQueries({
        queryKey: legalDocumentsKeys.active(publishedDocument.type)
      });

      // Update the specific document in cache
      queryClient.setQueryData(legalDocumentsKeys.detail(publishedDocument.id), publishedDocument);
    },
  });
};
